import { h } from 'vue';

import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ChannelLeaf from '../ChannelLeaf.vue';
import ViewLeaf from '../ViewLeaf.vue';

/**
 * Views: the connected inboxes followed by the saved conversation views, as
 * one flat top-level group - Text.com's last sidebar group, where a channel
 * and a saved filter are the same kind of thing, a lens on the queue. Flat
 * rather than nested sub-groups, which also drops the sub-group sort menu
 * since sorting only renders on nested sub-groups.
 */
export default ({
  t,
  accountScopedRoute,
  sortedInboxes,
  getInboxUnreadCount,
  sortedFolders,
  getFolderUnreadCount,
  currentUserId,
  isAdmin,
  onDeleteConversationView,
  onToggleConversationViewShared,
}) => ({
  name: 'Views',
  label: t('SIDEBAR.VIEWS'),
  icon: 'i-ph-mailbox',
  children: [
    ...sortedInboxes.map(inbox => ({
      name: `${inbox.name}-${inbox.id}`,
      label: inbox.name,
      badgeCount: getInboxUnreadCount(inbox.id),
      icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
      to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
      component: leafProps =>
        h(ChannelLeaf, {
          label: leafProps.label,
          active: leafProps.active,
          inbox,
          badgeCount: leafProps.badgeCount,
          badgeCountLabelKey: leafProps.badgeCountLabelKey,
        }),
    })),
    // Saved conversation views (CustomFilter records with
    // `filter_type: conversation`), pre-sorted by the folders sort preference.
    // The open conversation lives on a separate route below the folder, so
    // `activeOn` keeps the entry lit there. The leaf body swaps in the shared
    // ViewLeaf for the ⋯ manage menu; owners manage their own views, admins
    // additionally manage (and share/unshare) shared ones.
    ...sortedFolders.map(view => ({
      name: `View ${view.id}`,
      label: view.name,
      icon: view.shared ? 'i-lucide-users' : 'i-lucide-bookmark',
      badgeCount: getFolderUnreadCount(view.id),
      to: accountScopedRoute('folder_conversations', { id: view.id }),
      activeOn: ['conversations_through_folders'],
      component: leafProps =>
        h(ViewLeaf, {
          ...leafProps,
          shared: Boolean(view.shared),
          canManage:
            view.user_id === currentUserId ||
            (Boolean(view.shared) && Boolean(isAdmin)),
          canShare: Boolean(isAdmin),
          onDelete: () => onDeleteConversationView(view),
          onToggleShared: () => onToggleConversationViewShared(view),
        }),
    })),
    {
      name: 'Add Channel',
      label: t('SIDEBAR.ADD_CHANNEL'),
      icon: 'i-ph-plus',
      to: accountScopedRoute('settings_inbox_new'),
    },
  ],
});
